import {
    AlertInterval,
    ChartType,
    DeviceCare,
    Feature,
    MappingFunction,
    MetricField,
    MetricMapping,
    MetricThresholdProperties,
    State,
    ThresholdType,
    User,
} from '@myrtls/api-interfaces';
import { createSelector } from '@ngrx/store';
import { CustomSeriesOption, EChartsOption } from 'echarts';
import {
    CallbackDataParams,
    DataZoomComponentOption,
    XAXisOption,
    YAXisOption,
} from 'echarts/types/dist/shared';
import moment from 'moment';
import {
    DashboardRatioBarValueModel,
    DashboardSingleStatValueModel,
} from '../../shared/models/dashboard-value.model';
import { MetricGroup } from '../../shared/models/metric-group.enum';
import { MetricWithData } from '../../shared/models/metric-with-data.model';
import {
    SettingsDataModel,
    SettingsMetricModel,
    ThresholdToSymbolMap,
} from '../../shared/models/settings-data.model';
import {
    THRESHOLD_MARK_AREA_COLOR,
    X_AXIS_LABEL_FORMAT,
} from '../../shared/utils/constants';
import {
    getFieldAlias,
    tooltipFormatter,
    valueFormatter,
} from '../../shared/utils/utils';
import { $selectedCompanyId } from '../companies/companies.selectors';
import { AppState } from '../index';
import { SiteDetail, SitesState } from './sites.reducer';
import { AlertsWithMetricSettings } from '../../shared/models/alerts.model';
import {
    $alertsOnly,
    $deviceCareConfig,
    $isReport,
    $metricsWithData,
    $monitoringState,
    $siteAlerts,
} from '../monitoring/monitoring.selectors';

export interface TableOptions {
    columns: { name: string; alias: string }[];
    rows: {
        [key: string]: {
            value: string | { value: number | null; at: string }[];
            color?: string;
        };
    }[];
    noDataText?: string;
}

export const $sitesState = (state: AppState): SitesState => state.sites;

export const $sitesLoading = createSelector(
    $sitesState,
    ({ loading }): boolean => loading,
);

export const $sitesLoaded = createSelector(
    $sitesState,
    ({ loaded }): boolean => loaded,
);

export const $sites = createSelector(
    $sitesState,
    ({ sites }): { [siteId: string]: SiteDetail } => sites,
);

export const $allSitesList = createSelector(
    $sitesState,
    (state): SiteDetail[] =>
        Object.keys(state.sites).reduce(
            (acc: SiteDetail[], key) => [...acc, state.sites[key]],
            [],
        ),
);

export const $sitesList = createSelector(
    $allSitesList,
    $selectedCompanyId,
    (sites, companyId): SiteDetail[] =>
        (sites || []).filter(site => site.companyId === companyId),
);

export const $selectedSiteId = createSelector(
    $sitesState,
    ({ selectedSiteId }): string | null => selectedSiteId,
);

export const $site = createSelector(
    $sites,
    $selectedSiteId,
    (sites, id): SiteDetail | null => (id && sites[id]) || null,
);

export const $nextReview = createSelector(
    $site,
    site => site?.nextReviewDate || null,
);

export const $siteUsers = createSelector(
    $site,
    (site): User[] => site?.users || [],
);

export const $selectedSite = createSelector(
    $sites,
    $selectedSiteId,
    (sites, selectedSiteId: string | null): SiteDetail | null =>
        (selectedSiteId && sites[selectedSiteId]) || null,
);

export const $currentLicense = createSelector(
    $selectedSite,
    site => site?.license || null,
);

export const $selectedSiteName = createSelector(
    $selectedSite,
    (site: SiteDetail | null): string | null =>
        site && site.id && site.name ? 'S' + site.id + ' - ' + site.name : null,
);

export const $siteFeatures = createSelector(
    $selectedSite,
    (site): Feature[] => site?.features || [],
);

export const $selectedSiteLicenseRaw = createSelector(
    $selectedSite,
    site => site?.licenseRaw || null,
);

export const $siteFeature = (feature: string) =>
    createSelector($selectedSite, (site): Feature | null => {
        return (
            site?.features?.find(
                foundFeature => foundFeature.name === feature,
            ) || null
        );
    });

export const $settingsMetrics = createSelector(
    $metricsWithData,
    (metrics: MetricWithData[]): SettingsMetricModel[] =>
        (metrics || []).map(metric => ({
            id: metric.id,
            label: metric.name,
            nameSuffix: metric.nameSuffix,
            state: metric.state,
            alert: metric.alert,
            report: metric.report,
            dashboard: metric.dashboard,
            history: metric.history,
            hasDashboardTemplate: metric.dashboardTemplate !== undefined,
            hasHistoryTemplate: metric.historyTemplate !== undefined,
            hasAlertTemplate: metric.alertTemplate !== undefined,
            overridable: metric.overridable || false,
            alertTemplate: metric.alertTemplate,
            mappings: metric.dashboardTemplate
                ? metric.dashboardTemplate.parameters.mappings.filter(
                      mapping => mapping.from !== 0,
                  )
                : [],
            threshold: metric.thresholdProperties
                ? {
                      value:
                          metric.thresholdValue /
                          (metric?.historyTemplate?.parameters?.abbreviation
                              ?.value || 1),
                      valueAbbreviation:
                          metric?.historyTemplate?.parameters?.abbreviation
                              ?.value || 1,
                      unit: `${
                          metric.historyTemplate?.parameters?.abbreviation
                              ?.prefix || ''
                      }${
                          metric?.thresholdProperties?.unit ||
                          metric?.historyTemplate?.parameters?.unit ||
                          metric?.dashboardTemplate?.parameters?.unit ||
                          ''
                      }`,
                      symbol:
                          ThresholdToSymbolMap[
                              metric.thresholdProperties.type
                          ] || '',
                      state: metric.thresholdProperties.state,
                      editable: metric.thresholdProperties.editable,
                      tooltip: metric.thresholdProperties.tooltip,
                  }
                : undefined,
        })),
);

export const $settingsData = createSelector(
    $settingsMetrics,
    $deviceCareConfig,
    (
        metrics: SettingsMetricModel[],
        deviceCare: DeviceCare | null | undefined,
    ): SettingsDataModel => ({
        metrics,
        deviceCare: deviceCare ?? undefined,
    }),
);

export const $dcActivation = createSelector($monitoringState, state => {
    if (!state.deviceCare) {
        return null;
    }

    return state.deviceCare.dcActivation;
});

export const $siteAlertDays = createSelector(
    $siteAlerts,
    $dcActivation,
    $settingsMetrics,
    (alerts, activation, settingsMetrics) => {
        if (!alerts || !activation || settingsMetrics.length === 0) {
            return {};
        }

        const today = moment();
        const dates: AlertsWithMetricSettings = {};

        for (const alert of alerts) {
            const from = moment(alert.from);
            const to = alert.to ? moment(alert.to) : today;

            const metricSettings = settingsMetrics.find(
                metric => metric.id === alert.metric,
            );

            if (!metricSettings) {
                continue;
            }

            while (!from.isAfter(to, 'days')) {
                const currentDayFormatted = from.format('YYYY-MM-DD');

                if (!dates[currentDayFormatted]) {
                    dates[currentDayFormatted] = {
                        partial: false,
                        alerts: [
                            {
                                ...alert,
                                metric: metricSettings.id,
                                metricSettings,
                            },
                        ],
                        state: State.OK,
                    };
                } else {
                    dates[currentDayFormatted].alerts.push({
                        ...alert,
                        metric: metricSettings.id,
                        metricSettings,
                    });
                }

                dates[currentDayFormatted].state = State.ERROR;
                from.add(1, 'day');
            }
        }

        const dcActivationDate = activation ? moment.unix(activation) : null;

        if (dcActivationDate) {
            const maxHistoryDate = moment().subtract(6, 'months');
            const currentDay = dcActivationDate.isBefore(maxHistoryDate)
                ? maxHistoryDate
                : dcActivationDate;

            while (currentDay.isSameOrBefore(today, 'days')) {
                const currentDayFormatted = currentDay.format('YYYY-MM-DD');

                if (!dates[currentDayFormatted]) {
                    dates[currentDayFormatted] = {
                        partial: false,
                        alerts: [],
                        state: State.OK,
                    };
                }

                currentDay.add(1, 'day');
            }
        }

        const todayFormatted = today.format('YYYY-MM-DD');

        if (dates[todayFormatted]) {
            dates[todayFormatted].partial = true;
        }

        return dates;
    },
);

export const $sitePendingAlerts = createSelector(
    $siteAlerts,
    $settingsMetrics,
    (alerts: AlertInterval[] | null, settingsMetrics) => {
        if (alerts === null) {
            return null;
        }

        const pendingAlerts: AlertInterval[] = [];

        alerts
            .filter(alert => alert.to === null)
            .map(alert => {
                const metric = settingsMetrics.find(
                    metric => metric.id === alert.metric,
                );

                if (metric) {
                    pendingAlerts.push({
                        metric: metric.label,
                        metadata: alert.metadata,
                        from: alert.from,
                        to: alert.to,
                    });
                }
            });

        return pendingAlerts;
    },
);

export const $dashboardSingleStatData = createSelector(
    $metricsWithData,
    $selectedSiteId,
    (metrics: MetricWithData[], id): DashboardSingleStatValueModel[] =>
        (metrics || [])
            .filter(
                metric => metric.dashboardTemplate != null && metric.dashboard,
            )
            .filter(
                metric =>
                    metric.dashboardTemplate?.type === ChartType.SINGLE_STAT,
            )
            .map(metric => {
                const value = metric.dashboardData?.length
                    ? metric.dashboardData[0]._value
                    : null;

                return {
                    id: metric.id,
                    version: metric.version,
                    loading: metric.dashboardData === undefined,
                    color: getMetricValueColor(
                        value === undefined ? null : value,
                        metric.thresholdValue,
                        metric.dashboardTemplate?.parameters.mappings || [],
                        metric.thresholdProperties,
                    ),
                    value:
                        value === null
                            ? 'NO DATA'
                            : valueFormatter(
                                  value || 0,
                                  metric.dashboardTemplate?.parameters
                                      .mappings || [],
                                  metric.dashboardTemplate?.parameters
                                      .abbreviation,
                                  metric.dashboardTemplate?.parameters.unit,
                                  metric.dashboardTemplate?.parameters.format,
                              ),
                    info: metric.dashboardTemplate?.info?.replace(
                        /\$SITE_ID/gm,
                        id ? id : '',
                    ),
                    full: metric.dashboardTemplate?.full,
                    label: getMetricLabel(
                        metric.id,
                        metric.dashboardTemplate?.fields,
                    ),
                    nameSuffix: metric.nameSuffix,
                    threshold: metric.dashboardTemplate?.showThreshold
                        ? metric.thresholdValue.toString()
                        : undefined,
                };
            }),
);

export const $dashboardStackedChartData = createSelector(
    $metricsWithData,
    $selectedSiteId,
    (metrics: MetricWithData[], id): DashboardRatioBarValueModel[] =>
        (metrics || [])
            .filter(
                metric =>
                    metric.dashboardData !== null &&
                    metric.dashboardTemplate != null &&
                    metric.dashboard,
            )
            .filter(
                metric =>
                    metric.dashboardTemplate?.type === ChartType.STACKED_BAR,
            )
            .map(metric => {
                if (!metric) {
                    return { label: '', data: [] };
                }

                if (!metric.dashboardData) {
                    return { label: metric.name, data: [] };
                }

                const counter: {
                    [key: string]: {
                        count: number;
                        properties: MetricMapping;
                    };
                } = {};

                if (metric.dashboardTemplate?.parameters.mappings) {
                    for (const mapping of metric.dashboardTemplate?.parameters
                        .mappings || []) {
                        counter[mapping.from.toString()] = {
                            count: 0,
                            properties: mapping,
                        };
                    }
                }

                (metric.dashboardData || []).forEach(item => {
                    if (item._value === undefined) {
                        return;
                    }

                    if (counter[item._value] === undefined) {
                        counter[item._value] = {
                            count: 0,
                            properties: {
                                from: item._value,
                                to: `${item._value}`,
                                color: '#a4aab2',
                            },
                        };
                    }

                    counter[item._value].count++;
                });

                const total = metric.dashboardData.length;

                if (total === 0) {
                    counter[-1] = {
                        count: 0,
                        properties: {
                            from: -1,
                            to: 'NO DATA',
                        },
                    };
                }

                return {
                    id: metric.id,
                    version: metric.version,
                    label: metric.name,
                    nameSuffix: metric.nameSuffix,
                    info: metric.dashboardTemplate?.info?.replace(
                        /\$SITE_ID/gm,
                        id ? id : '',
                    ),
                    data: Object.keys(counter)
                        .map(key => {
                            let value =
                                Math.round(
                                    (counter[key].count / total) * 1000,
                                ) / 10;

                            if (isNaN(value)) {
                                value = 0;
                            }

                            return {
                                label: counter[key].properties.to,
                                color: counter[key].properties.color
                                    ? counter[key].properties.color
                                    : '#a4aab2',
                                value,
                                count: counter[key].count,
                            };
                        })
                        .filter(
                            item =>
                                item.value !== 0 || item.label === 'NO DATA',
                        )
                        .map(item => ({
                            label: item.label,
                            color: item.color,
                            value: `${item.value}`,
                            count: `${item.count}`,
                        })),
                };
            }),
);

export const $chartOptions = (showSliders = true) =>
    createSelector(
        $metricsWithData,
        $alertsOnly,
        $isReport,
        (
            metrics,
            alertsOnly,
            isReport,
        ): (
            | {
                  id: string;
                  name: string;
                  nameSuffix?: string;
                  info?: string;
                  group: MetricGroup;
                  options?: EChartsOption;
                  tableOptions?: TableOptions;
              }
            | null
            | false
        )[] => {
            return metrics
                .filter(
                    ({ historyTemplate, history }) =>
                        historyTemplate !== undefined && history,
                )
                .map(
                    ({
                        id,
                        name,
                        nameSuffix,
                        historyTemplate,
                        group,
                        data,
                        thresholdProperties,
                        thresholdValue,
                        from,
                        to,
                        groupLabel,
                        labelGroups,
                    }) => {
                        if (!data) {
                            return null;
                        }

                        if (!historyTemplate) {
                            return false;
                        }

                        if (historyTemplate.type === ChartType.TABLE) {
                            if (historyTemplate.parameters.multiline) {
                                if (!groupLabel) {
                                    return false;
                                }

                                data = Object.values(
                                    data.reduce<{
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        [key: string]: any;
                                    }>((acc, item) => {
                                        if (!item[groupLabel]) {
                                            return acc;
                                        }

                                        const group = item[
                                            groupLabel
                                        ] as string;

                                        if (!acc[group]) {
                                            acc[group] = {
                                                [groupLabel]: group,
                                                [historyTemplate.valueField]:
                                                    [],
                                            };
                                        }

                                        if (
                                            item[historyTemplate.valueField] !==
                                                null &&
                                            labelGroups
                                        ) {
                                            const labelGroupsWithoutGroupLabel =
                                                labelGroups.filter(
                                                    filteredGroup =>
                                                        filteredGroup !==
                                                        groupLabel,
                                                );

                                            for (const labelGroup of labelGroupsWithoutGroupLabel) {
                                                acc[group][labelGroup] =
                                                    item[labelGroup];
                                            }
                                        }

                                        acc[group][
                                            historyTemplate.valueField
                                        ].push({
                                            value: item[
                                                historyTemplate.valueField
                                            ],
                                            at: item._time,
                                        });
                                        return acc;
                                    }, {}),
                                );
                            }

                            return {
                                id,
                                name,
                                nameSuffix,
                                info: historyTemplate.info,
                                group: group as MetricGroup,
                                tableOptions: {
                                    noDataText: historyTemplate.noDataText,
                                    columns: historyTemplate.fields,
                                    rows: data
                                        .map(
                                            (item: {
                                                [key: string]:
                                                    | string
                                                    | number
                                                    | {
                                                          value: number | null;
                                                          at: string;
                                                      }[]
                                                    | undefined;
                                            }) => {
                                                const parsedData: {
                                                    [key: string]: {
                                                        value:
                                                            | string
                                                            | {
                                                                  value:
                                                                      | number
                                                                      | null;
                                                                  at: string;
                                                              }[];
                                                        originalValue:
                                                            | number
                                                            | string
                                                            | {
                                                                  value:
                                                                      | number
                                                                      | null;
                                                                  at: string;
                                                              }[];
                                                        color?: string;
                                                        colorMapping?: {
                                                            [
                                                                key: number
                                                            ]: string;
                                                            default: string;
                                                        };
                                                        alerting?: boolean;
                                                    };
                                                } = {};

                                                for (const column of historyTemplate.fields) {
                                                    const value: {
                                                        value:
                                                            | string
                                                            | {
                                                                  value:
                                                                      | number
                                                                      | null;
                                                                  at: string;
                                                              }[];
                                                        originalValue:
                                                            | number
                                                            | string
                                                            | {
                                                                  value:
                                                                      | number
                                                                      | null;
                                                                  at: string;
                                                              }[];
                                                        color?: string;
                                                        colorMapping?: {
                                                            [
                                                                key: number
                                                            ]: string;
                                                            default: string;
                                                        };
                                                        alerting?: boolean;
                                                    } = {
                                                        value: '',
                                                        originalValue:
                                                            item[
                                                                column.name
                                                            ] === undefined
                                                                ? ''
                                                                : (item[
                                                                      column
                                                                          .name
                                                                  ] as
                                                                      | string
                                                                      | number
                                                                      | {
                                                                            value:
                                                                                | number
                                                                                | null;
                                                                            at: string;
                                                                        }[]),
                                                    };

                                                    let mapped = false;
                                                    const currentItem =
                                                        item[column.name];

                                                    if (
                                                        typeof currentItem !==
                                                        'object'
                                                    ) {
                                                        if (
                                                            Array.isArray(
                                                                column.mappings,
                                                            )
                                                        ) {
                                                            for (const mapping of column.mappings) {
                                                                if (
                                                                    currentItem ===
                                                                    mapping.from
                                                                ) {
                                                                    value.value =
                                                                        mapping.to;
                                                                    value.color =
                                                                        mapping.color;
                                                                    value.alerting =
                                                                        mapping.alerting;
                                                                    mapped =
                                                                        true;
                                                                    break;
                                                                }
                                                            }
                                                        } else {
                                                            switch (
                                                                column.mappings
                                                            ) {
                                                                case MappingFunction.TIME:
                                                                    value.value =
                                                                        moment(
                                                                            currentItem,
                                                                        ).format(
                                                                            'YYYY/MM/DD HH:mm:ss',
                                                                        );

                                                                    mapped =
                                                                        true;
                                                                    break;
                                                                case MappingFunction.THRESHOLD:
                                                                    if (
                                                                        typeof currentItem ===
                                                                            'number' &&
                                                                        typeof thresholdValue ===
                                                                            'number'
                                                                    ) {
                                                                        if (
                                                                            currentItem >
                                                                            thresholdValue
                                                                        ) {
                                                                            value.value =
                                                                                '' +
                                                                                currentItem;
                                                                            value.color =
                                                                                'red';
                                                                            value.alerting =
                                                                                true;
                                                                        } else {
                                                                            value.value =
                                                                                '' +
                                                                                currentItem;
                                                                            value.color =
                                                                                'green';
                                                                        }
                                                                    }

                                                                    mapped =
                                                                        true;
                                                                    break;
                                                                case MappingFunction.DURATION_THRESHOLD:
                                                                    if (
                                                                        typeof currentItem ===
                                                                            'number' &&
                                                                        typeof thresholdValue ===
                                                                            'number'
                                                                    ) {
                                                                        value.value =
                                                                            moment
                                                                                .duration(
                                                                                    currentItem,
                                                                                    'minutes',
                                                                                )
                                                                                .humanize();

                                                                        if (
                                                                            currentItem >
                                                                            thresholdValue
                                                                        ) {
                                                                            value.color =
                                                                                'red';
                                                                            value.alerting =
                                                                                true;
                                                                        } else {
                                                                            value.color =
                                                                                'green';
                                                                        }
                                                                    }

                                                                    mapped =
                                                                        true;
                                                                    break;
                                                            }
                                                        }

                                                        if (!mapped) {
                                                            value.value =
                                                                '' +
                                                                (!item[
                                                                    column.name
                                                                ]
                                                                    ? ''
                                                                    : item[
                                                                          column
                                                                              .name
                                                                      ]);
                                                        }

                                                        if (column.unit) {
                                                            value.value =
                                                                value.value +
                                                                ' ' +
                                                                column.unit;
                                                        }
                                                    } else {
                                                        value.value =
                                                            currentItem;

                                                        if (
                                                            column.name ===
                                                                historyTemplate.valueField &&
                                                            Array.isArray(
                                                                column.mappings,
                                                            )
                                                        ) {
                                                            for (const mapping of column.mappings) {
                                                                if (
                                                                    !value.colorMapping
                                                                ) {
                                                                    value.colorMapping =
                                                                        {
                                                                            default:
                                                                                'dark-400',
                                                                        };
                                                                }

                                                                if (
                                                                    mapping.color
                                                                ) {
                                                                    value.colorMapping[
                                                                        mapping.from
                                                                    ] =
                                                                        mapping.color;
                                                                }

                                                                if (
                                                                    !value.alerting &&
                                                                    mapping.alerting
                                                                ) {
                                                                    for (const currentValue of currentItem) {
                                                                        if (
                                                                            currentValue.value ===
                                                                            mapping.from
                                                                        ) {
                                                                            value.alerting =
                                                                                true;
                                                                            break;
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }

                                                    parsedData[column.name] =
                                                        value;
                                                }

                                                return parsedData;
                                            },
                                        )
                                        .filter(data => {
                                            if (
                                                data[historyTemplate.valueField]
                                                    .originalValue === ''
                                            ) {
                                                return false;
                                            }

                                            if (!alertsOnly) {
                                                return true;
                                            }

                                            return (
                                                data[historyTemplate.valueField]
                                                    ?.alerting === true
                                            );
                                        })
                                        .sort((a, b) => {
                                            if (
                                                !historyTemplate.parameters
                                                    .sorting ||
                                                historyTemplate.parameters
                                                    .sorting.length === 0
                                            ) {
                                                return 0;
                                            }

                                            for (const sort of historyTemplate
                                                .parameters.sorting) {
                                                const aValue =
                                                    a[sort.field]
                                                        ?.originalValue;
                                                const bValue =
                                                    b[sort.field]
                                                        ?.originalValue;

                                                let result: number;

                                                if (
                                                    typeof aValue ===
                                                        'object' &&
                                                    typeof bValue === 'object'
                                                ) {
                                                    const aError =
                                                        aValue.reduce(
                                                            (acc, val) => {
                                                                if (
                                                                    val.value ===
                                                                    0
                                                                ) {
                                                                    return (
                                                                        acc + 1
                                                                    );
                                                                }

                                                                return acc;
                                                            },
                                                            0,
                                                        );

                                                    const bError =
                                                        bValue.reduce(
                                                            (acc, val) => {
                                                                if (
                                                                    val.value ===
                                                                    0
                                                                ) {
                                                                    return (
                                                                        acc + 1
                                                                    );
                                                                }

                                                                return acc;
                                                            },
                                                            0,
                                                        );

                                                    result = aError - bError;
                                                } else if (
                                                    typeof aValue ===
                                                        'string' &&
                                                    typeof bValue === 'string'
                                                ) {
                                                    result =
                                                        aValue.localeCompare(
                                                            bValue,
                                                        );
                                                } else if (
                                                    typeof aValue ===
                                                        'number' &&
                                                    typeof bValue === 'number'
                                                ) {
                                                    result = aValue - bValue;
                                                } else {
                                                    result = 0;
                                                }

                                                if (
                                                    aValue === '' &&
                                                    bValue !== ''
                                                ) {
                                                    result = 1;
                                                } else if (
                                                    aValue !== '' &&
                                                    bValue === ''
                                                ) {
                                                    result = -1;
                                                }

                                                if (sort.direction === 'desc') {
                                                    result = -result;
                                                }

                                                if (result !== 0) {
                                                    return result;
                                                }
                                            }

                                            return 0;
                                        }),
                                },
                            };
                        }

                        const {
                            type,
                            fields,
                            parameters: {
                                min,
                                max,
                                unit,
                                interval,
                                mappings,
                                abbreviation,
                                format,
                            },
                        } = historyTemplate;

                        const normalizedData = data.reduce(
                            (
                                acc: {
                                    [key: string]: (
                                        | string
                                        | number
                                        | undefined
                                    )[][];
                                },
                                fragment,
                            ) => {
                                const fieldAlias = getFieldAlias(
                                    fragment,
                                    fields,
                                );
                                return {
                                    ...acc,
                                    [fieldAlias]: [
                                        ...(acc[fieldAlias] || []),
                                        [fragment._time, fragment._value],
                                    ],
                                };
                            },
                            {},
                        );

                        const series: CustomSeriesOption[] = [
                            ...Object.entries(normalizedData || {}).map(
                                ([name, data]) => ({
                                    name,
                                    type,
                                    data,
                                    showSymbol: false,
                                }),
                            ),
                            {
                                // this type conversion is required because of echarts bad type definitions
                                type: ChartType.LINE as unknown,
                                markArea:
                                    thresholdProperties && data.length > 0
                                        ? {
                                              itemStyle: {
                                                  color: THRESHOLD_MARK_AREA_COLOR,
                                              },
                                              data: [
                                                  thresholdProperties.type ===
                                                  ThresholdType.ABOVE
                                                      ? [
                                                            {
                                                                yAxis: thresholdValue,
                                                            },
                                                            {},
                                                        ]
                                                      : [
                                                            {},
                                                            {
                                                                yAxis: thresholdValue,
                                                            },
                                                        ],
                                              ],
                                          }
                                        : {},
                            },
                        ] as CustomSeriesOption[];

                        const dataZoom = (
                            showSliders
                                ? [
                                      // horizontal scroll
                                      {
                                          type: 'slider',
                                          xAxisIndex: 0,
                                          filterMode: 'weakFilter',
                                          height: 20,
                                          bottom: 20,
                                          start: 0,
                                          end: 100,
                                          handleIcon:
                                              'path://M10.7,11.9H9.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4h1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
                                          handleSize: '80%',
                                          showDetail: false,
                                      },
                                  ]
                                : []
                        ) as DataZoomComponentOption[];

                        const chartData: {
                            id: string;
                            name: string;
                            nameSuffix?: string;
                            group: MetricGroup;
                            info?: string;
                            options: EChartsOption;
                        } = {
                            id,
                            name,
                            nameSuffix,
                            group: group as MetricGroup,
                            info: historyTemplate.info,
                            options: {
                                legend: {
                                    show:
                                        historyTemplate.showLegend ===
                                            undefined ||
                                        historyTemplate.showLegend,
                                    type: 'scroll',
                                    orient: 'horizontal',
                                    align: 'left',
                                    data: Object.keys(normalizedData || {}),
                                },
                                tooltip: {
                                    trigger: 'axis',
                                    formatter: (params: unknown) => {
                                        return tooltipFormatter(
                                            // this type conversion is required because of echarts bad type definitions
                                            params as CallbackDataParams[],
                                            id,
                                            mappings,
                                            abbreviation,
                                            unit,
                                            false,
                                            format,
                                        );
                                    },
                                },
                                xAxis: {
                                    type: 'time',
                                    min: from,
                                    max: to,
                                    axisLine: {
                                        show: true,
                                    },
                                    axisLabel: {
                                        rotate: 0,
                                        margin: 15,
                                        hideOverlap: true,
                                        width: 150,
                                        align: 'left',
                                        formatter: (value: number) =>
                                            moment(value).format(
                                                X_AXIS_LABEL_FORMAT,
                                            ),
                                    },
                                    silent: false,
                                    splitLine: {
                                        show: false,
                                    },
                                },
                                yAxis: {
                                    type: 'value',
                                    scale: true,
                                    min,
                                    max,
                                    minInterval: interval,
                                    axisLine: {
                                        show: true,
                                    },
                                    axisLabel: {
                                        margin: 20,
                                        formatter: (value: number) =>
                                            valueFormatter(
                                                value,
                                                mappings,
                                                abbreviation,
                                                unit,
                                                format,
                                            ),
                                    },
                                },
                                grid: {
                                    top: 50,
                                    bottom: 50,
                                    left: 10,
                                    right: 10,
                                    containLabel: true,
                                },
                                dataZoom,
                                series,
                                ...(isReport
                                    ? { animation: false }
                                    : {
                                          animationEasing: 'elasticOut',
                                          animationDelayUpdate: (idx: number) => idx * 5,
                                      }),
                            },
                        };

                        if (data.length === 0) {
                            chartData.options.title = {
                                show: true,
                                textStyle: {
                                    color: 'grey',
                                    fontSize: 20,
                                },
                                text: 'NO DATA',
                                left: 'center',
                                top: 'center',
                            };

                            (chartData.options.xAxis as XAXisOption).show =
                                false;
                            (chartData.options.yAxis as YAXisOption).show =
                                false;
                        }

                        return chartData;
                    },
                );
        },
    );

function getMetricLabel(id: string, fields: MetricField[] | undefined) {
    if (!fields || fields.length !== 1) {
        return id.toUpperCase();
    }

    return fields[0].alias;
}

function getMetricValueColor(
    value: number | null,
    thresholdValue: number,
    mappings: MetricMapping[],
    thresholdProperties?: MetricThresholdProperties,
): string {
    if (value === null) {
        return 'dark-400';
    }

    if (mappings.length) {
        const mapping = mappings.find(mapping => mapping.from === value);

        if (mapping) {
            return mapping.color || 'dark-400';
        }
    }

    if (thresholdProperties?.type === ThresholdType.ABOVE) {
        return value > thresholdValue ? 'error' : 'brand';
    }
    if (thresholdProperties?.type === ThresholdType.BELOW) {
        return value < thresholdValue ? 'error' : 'brand';
    }

    return 'dark-400';
}
